import { Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { GetHomeCareReservationsForPatient } from '@virtual-care/client';
import {
  formatReservationDate,
  formatReservationTime,
} from '@virtual-care/isomorphic';
import { ReservationStatus } from '@virtual-care/ui';

type HomecareReservation = GetHomeCareReservationsForPatient['records'][number];

export function HomecareReservationCard({
  reservation,
}: {
  reservation: HomecareReservation;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formattedDate = formatReservationDate(reservation);
  const formattedTime = formatReservationTime(reservation);

  const vendorName =
    reservation.vendor.vendorType === 'INDIVIDUAL'
      ? `${reservation.vendor.individualProfile?.firstName ?? ''} ${reservation.vendor.individualProfile?.lastName ?? ''}`.trim() ||
        t('common.notSpecified')
      : (reservation.vendor.clinicProfile?.clinicName ??
        t('common.notSpecified'));

  return (
    <div
      className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      onClick={() => navigate(`/homecare/reservations/${reservation.id}`)}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-chart-3 flex h-6 w-6 items-center justify-center rounded-full">
            <Home className="h-4 text-white" />
          </div>
          <div className="flex flex-col items-start gap-1">
            <span className="font-bold text-black">
              {t('cards.homecareReservation.prefix')} #{reservation.counter}
            </span>
            <ReservationStatus.Root status={reservation.status}>
              <ReservationStatus.Indicator />
              <ReservationStatus.Label render={(meta) => t(meta.key)} />
            </ReservationStatus.Root>
          </div>
        </div>
      </div>
      <div className="space-y-3 text-sm">
        <p>
          <span className="text-muted-foreground">
            {t('cards.homecareReservation.service')}:
          </span>{' '}
          <span className="font-semibold">
            {reservation.vendorService.service.name}
          </span>
        </p>
        <p>
          <span className="text-muted-foreground">
            {t('cards.homecareReservation.provider')}:
          </span>{' '}
          <span className="font-semibold">{vendorName}</span>
        </p>
        <p>
          <span className="text-muted-foreground">
            {t('cards.homecareReservation.date')}:
          </span>{' '}
          <span className="font-semibold">{formattedDate}</span>
        </p>
        <p>
          <span className="text-muted-foreground">
            {t('cards.homecareReservation.time')}:
          </span>{' '}
          <span className="font-semibold">{formattedTime}</span>
        </p>
      </div>
    </div>
  );
}
